import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import { weekStartOf } from '@components/systems/settlement/settlement.format'

import type { ApiGetDataStatus, SettlementStateType } from '@redux/types'
import {
	SettlementRowType,
	SettlementMonthSummaryType,
	SettlementPatchType,
	SettlementBulkType,
} from '@schemas'

const now = dayjs()

const initialState: SettlementStateType = {
	unit: 'month',
	week: weekStartOf(now.toDate()),
	year: now.format('YYYY'),
	summary: [],
	summaryStatus: 'init',
	month: now.format('YYYY-MM'),
	currency: 'all',
	rows: [],
	rowsStatus: 'init',
	selectedOrderIds: [],
	savingOrderIds: [],
	bulkSaving: false,
	triggerInit: false,
	triggerUpdate: null,
	triggerBulk: null,
	triggerMarkPaid: null,
}

/** 정산 — DISPATCH_PLAN §6. persist 대상이 아니다 */
const slice = createSlice({
	name: 'settlement',
	initialState,
	reducers: {
		TRIGGER_INIT_SETTLEMENT_PAGE: (
			state: SettlementStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerInit = action.payload
		},
		YEAR: (state: SettlementStateType, action: PayloadAction<string>) => {
			state.year = action.payload
		},
		SUMMARY: (
			state: SettlementStateType,
			action: PayloadAction<SettlementMonthSummaryType[]>,
		) => {
			state.summary = action.payload
		},
		SUMMARY_STATUS: (
			state: SettlementStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.summaryStatus = action.payload
		},
		MONTH: (state: SettlementStateType, action: PayloadAction<string>) => {
			state.month = action.payload
			state.selectedOrderIds = []
		},
		UNIT: (
			state: SettlementStateType,
			action: PayloadAction<SettlementStateType['unit']>,
		) => {
			state.unit = action.payload
			state.selectedOrderIds = []
		},
		WEEK: (state: SettlementStateType, action: PayloadAction<string>) => {
			state.week = action.payload
			state.selectedOrderIds = []
		},
		CURRENCY: (
			state: SettlementStateType,
			action: PayloadAction<SettlementStateType['currency']>,
		) => {
			state.currency = action.payload
			state.selectedOrderIds = []
		},
		ROWS: (state: SettlementStateType, action: PayloadAction<SettlementRowType[]>) => {
			state.rows = action.payload
		},
		/** 한 행만 바꿔 넣는다 — 체크 즉시 저장의 응답 */
		ROW: (state: SettlementStateType, action: PayloadAction<SettlementRowType>) => {
			const i = state.rows.findIndex(r => r.orderId === action.payload.orderId)
			if (i >= 0) state.rows[i] = action.payload
		},
		ROWS_STATUS: (
			state: SettlementStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.rowsStatus = action.payload
		},
		SELECTED_ORDER_IDS: (state: SettlementStateType, action: PayloadAction<string[]>) => {
			state.selectedOrderIds = action.payload
		},
		SAVING_ORDER_IDS: (state: SettlementStateType, action: PayloadAction<string[]>) => {
			state.savingOrderIds = action.payload
		},
		BULK_SAVING: (state: SettlementStateType, action: PayloadAction<boolean>) => {
			state.bulkSaving = action.payload
		},
		TRIGGER_UPDATE: (
			state: SettlementStateType,
			action: PayloadAction<{ orderId: string; body: SettlementPatchType } | null>,
		) => {
			state.triggerUpdate = action.payload
		},
		TRIGGER_BULK: (
			state: SettlementStateType,
			action: PayloadAction<SettlementBulkType | null>,
		) => {
			state.triggerBulk = action.payload
		},
		TRIGGER_MARK_PAID: (
			state: SettlementStateType,
			action: PayloadAction<string[] | null>,
		) => {
			state.triggerMarkPaid = action.payload
		},
	},
})

export const settlementActions = slice.actions
export default slice.reducer
